import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { IMAGES, PROJECT_CATEGORIES, PROJECT_STATUSES, statusColor, PIPELINE } from "@/data/site";
import PageHero from "@/components/PageHero";
import { Reveal, Overline } from "@/components/Reveal";
import { ArrowRight, MapPin, LandPlot, Zap, Layers } from "lucide-react";

export default function Projects() {
  useSeo("Development Projects | 12 Stone Development Group",
    "Explore 12 Stone Development Group's pipeline of data center, industrial, residential, agri-tech and mixed-use development opportunities across the Southeast.");
  const [projects, setProjects] = useState([]);
  const [cat, setCat] = useState("All");
  const [status, setStatus] = useState("All");
  const [pipeline, setPipeline] = useState(PIPELINE);

  useEffect(() => {
    const params = new URLSearchParams();
    if (cat !== "All") params.set("category", cat);
    if (status !== "All") params.set("status", status);
    api.get(`/projects?${params.toString()}`).then((r) => setProjects(r.data)).catch(() => {});
  }, [cat, status]);

  useEffect(() => {
    api.get("/content/pipeline").then((r) => { if (r.data?.states?.length) setPipeline(r.data); }).catch(() => {});
  }, []);

  const imgFor = (p) => {
    const first = (p.images || [])[0];
    if (!first) return IMAGES.aerialLand;
    return first.startsWith("http") ? first : fileUrl(first);
  };

  return (
    <div>
      <PageHero overline="Development Pipeline" title="Development Opportunities"
        subtitle="Strategic sites at the intersection of land, power, utilities and community growth."
        image={IMAGES.aerialLand} />

      {/* PROJECT PIPELINE — STATE PORTFOLIOS */}
      <section className="border-b border-border bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <Reveal>
            <Overline>Project Pipeline</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-foreground text-balance">
              State development portfolios under evaluation
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
              Land assembled across the Southeast, evaluated for large-load technology and infrastructure development.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
            {pipeline.states.map((s, i) => (
              <Reveal key={s.state} delay={i * 0.08}>
                <div className="rounded-xl border border-border bg-card p-7 h-full" data-testid={`pipeline-${s.state.toLowerCase()}`}>
                  <div className="flex items-center gap-2.5">
                    <MapPin className="h-5 w-5 text-accent" />
                    <h3 className="font-display text-xl font-semibold text-foreground">{s.state}</h3>
                  </div>
                  <div className="mt-6 grid grid-cols-3 gap-4">
                    <div>
                      <LandPlot className="h-4 w-4 text-[hsl(var(--sage))] mb-2" />
                      <div className="font-display text-2xl font-bold text-foreground">{s.acres}</div>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Land area</div>
                    </div>
                    <div>
                      <Layers className="h-4 w-4 text-[hsl(var(--sage))] mb-2" />
                      <div className="font-display text-2xl font-bold text-foreground">{s.parcels}</div>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Assembled</div>
                    </div>
                    <div>
                      <Zap className="h-4 w-4 text-accent mb-2" />
                      <div className="font-display text-2xl font-bold text-foreground">{s.power}</div>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Potential power</div>
                    </div>
                  </div>
                  <p className="mt-5 text-xs text-muted-foreground/90 leading-relaxed border-t border-border pt-4">
                    {pipeline.powerLabel}.
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* AGGREGATE TOTALS */}
          <Reveal delay={0.1}>
            <div className="mt-5 rounded-xl bg-primary text-white p-7 sm:p-8" data-testid="pipeline-totals">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent">Aggregate Portfolio</div>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div>
                  <div className="font-display text-3xl sm:text-4xl font-extrabold">{pipeline.totals.acres}</div>
                  <div className="text-sm text-white/70 mt-1">Total land under evaluation</div>
                </div>
                <div>
                  <div className="font-display text-3xl sm:text-4xl font-extrabold">{pipeline.totals.parcels}</div>
                  <div className="text-sm text-white/70 mt-1">Across two states</div>
                </div>
                <div>
                  <div className="font-display text-3xl sm:text-4xl font-extrabold">{pipeline.totals.power} <span className="text-white/60 text-xl font-bold">({pipeline.totals.powerAlt})</span></div>
                  <div className="text-sm text-white/70 mt-1">Potential power under review</div>
                </div>
              </div>
            </div>
          </Reveal>

          <p className="mt-5 text-xs italic text-muted-foreground max-w-3xl">{pipeline.disclaimer}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="flex flex-col gap-4 mb-10">
          <div className="flex flex-wrap gap-2" data-testid="category-filters">
            {["All", ...PROJECT_CATEGORIES].map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`filter-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`font-mono text-xs px-4 py-2 rounded-full border transition-colors ${cat === c ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:text-primary hover:border-border"}`}>
                {c}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2" data-testid="status-filters">
            {["All", ...PROJECT_STATUSES].map((s) => (
              <button key={s} onClick={() => setStatus(s)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${status === s ? "bg-primary text-white border-primary" : "border-border text-muted-foreground/70 hover:text-primary"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {projects.length === 0 ? (
          <p className="text-muted-foreground py-20 text-center">No projects match the current filters.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.05}>
                <Link to={p.slug === "project-metter-first" ? "/metter-first" : `/projects/${p.slug}`} data-testid={`project-card-${p.slug}`}
                  className="group block rounded-xl overflow-hidden border border-border bg-card hover:border-primary/40 transition-all h-full">
                  <div className="aspect-[16/10] overflow-hidden relative">
                    <img src={imgFor(p)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute top-3 left-3 font-mono text-[10px] px-2.5 py-1 rounded-full border backdrop-blur-md bg-primary/70 text-white border-transparent">{p.category}</span>
                  </div>
                  <div className="p-6">
                    <span className={`inline-block font-mono text-[10px] px-2.5 py-1 rounded-full border ${statusColor(p.status)}`}>{p.status}</span>
                    <h3 className="mt-3 font-display text-xl font-semibold text-foreground group-hover:text-primary transition-colors">{p.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{p.location} · {p.acreage}</p>
                    <p className="text-sm text-muted-foreground/80 mt-3 line-clamp-2">{p.summary}</p>
                    <div className="mt-4 flex items-center text-sm text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                      View project <ArrowRight className="ml-1.5 h-4 w-4" />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
